import { useRef, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import { useExpenses, useImportWorkbook, useRestore } from "../api/hooks";
import { errorText, useFlash } from "../components/Flash";

export function ImportPage() {
  const year = new Date().getFullYear();
  const importer = useImportWorkbook();
  const restore = useRestore();
  const expenses = useExpenses();
  const flash = useFlash();
  const navigate = useNavigate();
  const xlsx = useRef<HTMLInputElement>(null);
  const backup = useRef<HTMLInputElement>(null);

  const doImport = (e: FormEvent) => {
    e.preventDefault();
    const file = xlsx.current?.files?.[0];
    if (!file) return flash("Choose your .xlsm or .xlsx file first.", "error");
    importer.mutate(file, {
      onSuccess: (r) => {
        r.messages.forEach((m) => flash(m));
        navigate(`/year/${r.year ?? year}`);
      },
      onError: (err) => flash(errorText(err), "error"),
    });
  };

  const doRestore = (e: FormEvent) => {
    e.preventDefault();
    const file = backup.current?.files?.[0];
    if (!file) return flash("Choose a backup file first.", "error");
    if (!window.confirm("Replace all data with this backup?")) return;
    restore.mutate(file, {
      onSuccess: (r) => {
        r.messages.forEach((m) => flash(m));
        navigate("/");
      },
      onError: (err) => flash(errorText(err), "error"),
    });
  };

  return (
    <>
      <div className="page-head">
        <h1>Import and backup</h1>
      </div>
      <div className="two-col">
        <div className="panel">
          <h2>Import a year from your spreadsheet</h2>
          <p>
            Choose a yearly expenses file (.xlsm). Jobs, expenses, calendar colours, rates and DOIL history
            are read from it.
          </p>
          <p className="small muted">
            Importing replaces everything already stored for that year. Import your oldest year first: it
            becomes your starting point for DOIL and holiday balances.
          </p>
          <form onSubmit={doImport}>
            <label htmlFor="file">Spreadsheet</label>
            <input id="file" ref={xlsx} type="file" accept=".xlsm,.xlsx" required />
            <div className="actions">
              <button className="btn" type="submit" disabled={importer.isPending}>
                {importer.isPending ? "Importing…" : "Import"}
              </button>
            </div>
          </form>
        </div>

        <div className="panel">
          <h2>Backup</h2>
          <p>Download a copy of all your data now and then. The server also takes a nightly backup.</p>
          <div className="actions" style={{ marginTop: 0 }}>
            <a className="btn quiet" href="/api/backup">
              Download backup
            </a>
            {expenses && (
              <a className="btn quiet" href={`/api/export/${year}.csv`}>
                Download {year} expenses as CSV
              </a>
            )}
          </div>
          <h3 style={{ marginTop: "1.5rem" }}>Restore a backup</h3>
          <p className="small muted">
            Replaces all current data. Accepts a .json backup from this app, or a tracker.sqlite file from the
            old Work log app. A copy of the current data is kept on the server first.
          </p>
          <form onSubmit={doRestore}>
            <input ref={backup} type="file" accept=".json,.sqlite" required aria-label="Backup file" />
            <div className="actions">
              <button className="btn danger" type="submit" disabled={restore.isPending}>
                Restore backup
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
